import { EntityFolderFlatList, EntityFolderFlatListRecord, EntityFolderListRecord, EntityItemListRecord } from '@/entities/entityFolder';
import { emptyList } from '@/services/api';
import { computed, Ref } from 'vue';

export const pathSeparator = ',';
const maxUsersToShow = 3;

export const useTreeView = (list: Ref<EntityFolderFlatList>, collapsed: Ref<string[]>, deleted: Ref<string[]>) => {
  const convertToList = (entityFolders: EntityFolderListRecord[]) => {
    const flatList: EntityFolderFlatList = emptyList();
    entityFolders.forEach(entityFolder => {
      flatList.data.push(createEntityFolderEntry(entityFolder));

      entityFolder.data.entityItems.forEach(entity => {
        flatList.data.push(createEntityItemEntry(entityFolder, entity));
      });
    });

    list.value = flatList;
  };

  const createEntityFolderEntry = (entityFolder: EntityFolderListRecord): EntityFolderFlatListRecord => ({
    id: entityFolder.id,
    data: {
      title: entityFolder.data.title,
      path: entityFolder.data.path.join(pathSeparator),
      users: entityFolder.data.users,
      labels: entityFolder.data.labels,
      entityType: null,
    },
    authorizedTo: entityFolder.authorizedTo,
  });

  const createEntityItemEntry = (entityFolder: EntityFolderListRecord, entityItem: EntityItemListRecord): EntityFolderFlatListRecord => ({
    id: entityItem.id,
    data: {
      title: entityItem.data.title,
      path: entityFolder.data.path.join(pathSeparator) + pathSeparator + entityItem.id,
      users: entityItem.data.users,
      labels: [],
      entityType: entityItem.data.entityType,
    },
    authorizedTo: entityFolder.authorizedTo,
  });

  const collapseAll = () => {
    const collapsedList: string[] = [];
    list.value.data.forEach((listRecord: EntityFolderFlatListRecord) => {
      collapsedList.push(listRecord.data.path);
    });
    collapsed.value = collapsedList;
  };

  const expandAll = () => {
    collapsed.value = [];
  };

  const toggleChildren = (listRecord: EntityFolderFlatListRecord) => {
    const { path } = listRecord.data;
    if (!path) {
      return;
    }
    const isCollapsed = collapsed.value.includes(path);
    if (isCollapsed) {
      collapsed.value = collapsed.value.filter(item => item !== path);
      return;
    }

    collapsed.value.push(path);
  };

  const deleteChildren = ({ id, data: { path, entityType } }: EntityFolderFlatListRecord) => {
    if (!path || entityType) {
      return;
    }

    list.value.data.forEach(item => {
      if (item.data.path.endsWith(id.toString())) {
        deleted.value.push(item.data.path);
      }
    });
  };

  const filteredList = computed(() => list.value.data.filter((item: EntityFolderFlatListRecord) =>
    !Array.from([...collapsed.value, ...deleted.value]).some(filterPath =>
      item.data.path.startsWith(`${filterPath},`))));

  const hasChildren = (listRecord: EntityFolderFlatListRecord): boolean => list.value.data.some(
    (item: EntityFolderFlatListRecord) => item.data.path.startsWith(listRecord.data.path + pathSeparator),
  );

  const sliceUsersToShow = (listRecord: EntityFolderFlatListRecord): { usersToShow: string[]; remainingUsers: string[] } => ({
    usersToShow: listRecord.data.users.slice(0, maxUsersToShow).map(item => item.data.name),
    remainingUsers: listRecord.data.users.slice(maxUsersToShow).map(item => item.data.name),
  });

  return {
    convertToList,
    collapseAll,
    expandAll,
    toggleChildren,
    deleteChildren,
    filteredList,
    hasChildren,
    sliceUsersToShow,
  };
};
